import { z } from "zod";
import { DataSource } from "./enums.js";
import type { ExclusionCounts } from "./analysis.js";

/**
 * Valores recomendados y límites de los videos de estímulo. Una sola fuente para el aviso al
 * subir, las advertencias por archivo y la validación del servidor (ver docs/VIDEO_RESEARCH.md §4.1).
 */
export const VIDEO_LIMITS = {
  /** Límites duros: fuera de ellos el archivo se rechaza. */
  minDurationMs: 3_000,
  maxDurationMs: 180_000,
  /** Recomendados: fuera de ellos se acepta con advertencia. */
  recommendedMinDurationMs: 10_000,
  recommendedMaxDurationMs: 60_000,
  /** Resolución máxima de salida (mayores se reducen al convertir). */
  maxWidth: 1920,
  maxHeight: 1080,
  recommendedWidth: 1280,
  recommendedHeight: 720,
  /** Cuadros por segundo de salida (mayores se convierten a este valor). */
  targetFps: 30,
  recommendedMaxSizeMb: 25,
  recommendedMaxCount: 10,
} as const;

export const ALLOWED_VIDEO_MIME_TYPES = ["video/mp4", "video/webm", "video/quicktime"] as const;
export const ALLOWED_VIDEO_EXTENSIONS = [".mp4", ".m4v", ".webm", ".mov"] as const;

/** Texto del aviso de características recomendadas (diálogo previo a la subida). */
export const VIDEO_RECOMMENDATIONS: ReadonlyArray<{ label: string; text: string }> = [
  { label: "Formato", text: "MP4 (H.264). También se aceptan WebM y MOV; se convertirán automáticamente." },
  { label: "Resolución", text: "1280 × 720 (720p). Máximo 1920 × 1080; resoluciones mayores se reducirán." },
  { label: "Cuadros por segundo", text: "30 fps. Videos con más cuadros por segundo se convertirán a 30." },
  {
    label: "Duración",
    text: "Entre 10 y 60 segundos por video. Los videos largos aumentan la pérdida de calibración y el cansancio.",
  },
  { label: "Tamaño", text: "Hasta ~25 MB por video (un video 720p de 60 s pesa ~22 MB)." },
  { label: "Audio", text: "Opcional. Si el estudio no lo necesita, mejor sin audio." },
  {
    label: "Contenido",
    text: "Sin barras negras ni subtítulos incrustados, salvo que formen parte del estímulo. Usa material propio o con licencia.",
  },
  {
    label: "Cantidad",
    text: "Pocos videos por participante (p. ej. 5–10). Cada participante descarga todos antes de empezar.",
  },
];

/** Datos de reproducción de un ensayo de video (medidos en el navegador). */
export const VideoPlaybackMeta = z
  .object({
    /** Duración del medio según el navegador. */
    mediaDurationMs: z.number().min(0).max(600_000),
    /** Primer fotograma presentado, en ms relativos al inicio de la captura (t0 del ensayo). */
    firstFrameOffsetMs: z.number().min(-60_000).max(60_000).nullable(),
    /** Fotogramas presentados (requestVideoFrameCallback). */
    framesPresented: z.number().int().min(0).max(1_000_000),
    /** Fotogramas descartados según getVideoPlaybackQuality(). */
    framesDropped: z.number().int().min(0).max(1_000_000).nullable(),
    /** Veces que la reproducción se detuvo esperando datos. */
    stallCount: z.number().int().min(0).max(10_000),
    stallMs: z.number().min(0).max(600_000),
    /** La reproducción llegó al final (evento `ended`). */
    endedNaturally: z.boolean(),
  })
  .strict();
export type VideoPlaybackMeta = z.infer<typeof VideoPlaybackMeta>;

/**
 * Validación con punto en movimiento (seguimiento suave). Estima, por participante, el retraso
 * de las predicciones de mirada respecto al objetivo y el error espacial con ese retraso.
 */
export const PursuitMetrics = z
  .object({
    method: z.string().min(1).max(300),
    durationMs: z.number().min(0).max(120_000),
    totalSamples: z.number().int().min(0).max(100_000),
    samplesPerSecond: z.number().min(0).nullable(),
    /** Retraso estimado (ms): la mirada en t corresponde al objetivo en t − lag. */
    lagMs: z.number().min(-1000).max(3000).nullable(),
    /** Correlación media (x, y) entre mirada y objetivo con el retraso estimado. */
    correlation: z.number().min(-1).max(1).nullable(),
    meanErrorPx: z.number().min(0).nullable(),
    /** Error medio relativo a la diagonal del viewport (0–1). */
    meanErrorRelative: z.number().min(0).nullable(),
    viewportWidth: z.number().positive(),
    viewportHeight: z.number().positive(),
  })
  .strict();
export type PursuitMetrics = z.infer<typeof PursuitMetrics>;

export const PursuitRunCreate = z
  .object({
    startedAt: z.iso.datetime(),
    endedAt: z.iso.datetime(),
    metrics: PursuitMetrics,
  })
  .strict();
export type PursuitRunCreate = z.infer<typeof PursuitRunCreate>;

export interface PursuitRunDto extends PursuitRunCreate {
  recordedAt: string;
}

/** Datos de mirada por participante para el análisis temporal de un video (se agrega en el navegador). */
export const VideoGazeQuery = z
  .object({
    videoId: z.uuid(),
    groupA: z.uuid(),
    groupB: z.uuid(),
    source: DataSource.default("webgazer"),
    includeWarnQuality: z
      .enum(["true", "false"])
      .default("true")
      .transform((v) => v === "true"),
  })
  .strict();
export type VideoGazeQuery = z.infer<typeof VideoGazeQuery>;

export interface VideoGazeParticipantDto {
  sessionId: string;
  participantCode: string;
  /** Retraso medido en la validación con punto en movimiento (null si no hubo). */
  lagMs: number | null;
  /** Muestras válidas sobre el video: tiempo del video (ms) y posición normalizada [0,1]². */
  t: number[];
  x: number[];
  y: number[];
}

export interface VideoGazeGroupDto {
  groupId: string;
  groupCode: string;
  groupName: string;
  n: number;
  excluded: ExclusionCounts;
  participants: VideoGazeParticipantDto[];
}

export interface VideoGazeDto {
  generatedAt: string;
  source: DataSource;
  video: { id: string; filenameOriginal: string; durationMs: number; fps: number; naturalWidth: number; naturalHeight: number };
  groupA: VideoGazeGroupDto;
  groupB: VideoGazeGroupDto;
  warnings: string[];
}

/** Ventanas permitidas para el análisis temporal (ms). */
export const VIDEO_WINDOW_OPTIONS_MS = [500, 1000, 2000, 5000] as const;
export const DEFAULT_VIDEO_WINDOW_MS = 1000;
/** Retraso fijo de referencia (literatura: ~300 ms para WebGazer). */
export const REFERENCE_WEBGAZER_LAG_MS = 300;
